import { BorrowerDocumentationRequired, DocumentStatus, DocumentType, RequiredFile } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { DocumentationRequiredMap } from '@/components/modules/DocumentsValidation/maps/documentationRequired.map';
import { ValidationDeclaredDataMap } from '@/components/modules/DocumentsValidation/maps/validationDeclaredData.map';
import { ValidationStep, ValidationStepStatus } from '@/components/modules/DocumentsValidation/types/ValidationStep';

const validationSteps = Object.values(ValidationStep).map((step) => ({
  step,
  status: 'pending',
})) as ValidationStepStatus[];

export const useValidationSteps = () => {
  const { borrowerDocumentationRequired, borrowerOnboardingDetails } = useDocumentsValidationContext();
  const { documentType } = ValidationDeclaredDataMap.toDocumentIdData(borrowerOnboardingDetails);
  const initialSteps = getStepsStatus(validationSteps, borrowerDocumentationRequired, undefined, documentType);
  const initialStep = getFirstStep(initialSteps);

  const [steps, setSteps] = useState(initialSteps);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    setSteps(
      filterStepsByDocumentationRequired(
        getStepsStatus(validationSteps, borrowerDocumentationRequired, currentStep, documentType),
        borrowerDocumentationRequired,
        documentType,
      ),
    );
  }, [borrowerDocumentationRequired, currentStep]);

  const lastStepIndex = steps.length - 1;

  const resetValidationProcess = () => {
    setSteps(initialSteps);
    setCurrentStep(initialStep);
    setCompleted(false);
  };

  const onNext = () => {
    setCurrentStep((step) => {
      const currentStepIndex = steps.findIndex((s) => s.step === step.step);

      if (currentStepIndex === lastStepIndex) {
        setCompleted(true);
        return step;
      }

      // Find the next pending step after the current step
      const nextPendingStep = steps.find(
        (step, index) => (step.status === 'pending' || step.status === 'incidence') && index > currentStepIndex,
      );

      if (nextPendingStep) {
        return nextPendingStep;
      }

      return initialSteps[currentStepIndex + 1];
    });
  };

  return {
    step: currentStep,
    steps,
    onNext,
    completed,
    resetValidationProcess,
  };
};

const getFirstStep = (steps: ValidationStepStatus[]) => {
  return steps.find((step) => step.status === 'active') ?? steps[0];
};

const filterStepsByDocumentationRequired = (
  steps: ValidationStepStatus[],
  borrowerDocumentationRequired: BorrowerDocumentationRequired,
  documentType?: DocumentType,
) => {
  return steps.filter((step) => {
    if (
      step.step === ValidationStep.DOCUMENT_ID_VALIDATION ||
      step.step === ValidationStep.VIDEO_ID_VALIDATION ||
      step.step === ValidationStep.DOCUMENT_NUMBER_VALIDATION ||
      step.step === ValidationStep.CONFIRMATION
    ) {
      return true;
    }

    const filesByStep = DocumentationRequiredMap.getFilesByStep(borrowerDocumentationRequired, step.step, documentType);
    return filesByStep && filesByStep.length > 0;
  });
};

const checkIfAnyDocumentHasGivenStatus = (
  borrowerDocumentationRequired: BorrowerDocumentationRequired,
  filesByStep: RequiredFile[],
  documentStatuses: DocumentStatus[],
) => {
  return borrowerDocumentationRequired.requiredFiles?.find((file) =>
    filesByStep.some((fileByStep) => fileByStep.name === file.name && documentStatuses.includes(file.status)),
  )?.status;
};

const getStepsStatus = (
  steps: ValidationStepStatus[],
  borrowerDocumentationRequired: BorrowerDocumentationRequired,
  currentStep?: ValidationStepStatus,
  documentType?: DocumentType,
): ValidationStepStatus[] => {
  const currentStepIndex = currentStep ? steps.findIndex((step) => step.step === currentStep.step) : 0;

  const newSteps = steps.map((step, index) => {
    const filesByStep = DocumentationRequiredMap.getFilesByStep(borrowerDocumentationRequired, step.step, documentType);

    if (index < currentStepIndex) {
      if (filesByStep) {
        const currentStepStatus = checkIfAnyDocumentHasGivenStatus(borrowerDocumentationRequired, filesByStep, [
          DocumentStatus.INCIDENCE,
          DocumentStatus.PENDING,
        ]);

        return {
          step: step.step,
          status: currentStepStatus ?? 'completed',
        };
      }

      return {
        step: step.step,
        status: 'completed',
      };
    }

    if (index === currentStepIndex) {
      return {
        step: step.step,
        status: 'active',
      };
    }

    if (!filesByStep) {
      return step;
    }

    const allValidated =
      filesByStep.length > 0 ? filesByStep.every((file) => file.status === DocumentStatus.VALIDATED) : false;
    const anyFileInIncidence =
      filesByStep.length > 0 ? filesByStep.some((file) => file.status === DocumentStatus.INCIDENCE) : false;

    return {
      step: step.step,
      status: anyFileInIncidence ? 'incidence' : allValidated ? 'completed' : 'pending',
    };
  }) as ValidationStepStatus[];

  return newSteps;
};
